import * as XLSX from 'xlsx';

export interface ValidationResult {
  isValid: boolean;
  modifiedFile: Blob | null;
  errorMessage?: string;
}

export class SpreadsheetValidateService {
  private readonly validMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel'                                           // .xls
  ];

  private readonly zipSignature = '50 4b 03 04';

  // Public method to validate spreadsheet file
  public async validateSpreadsheet(file: Blob, fileName: string): Promise<ValidationResult> {
    if (this.hasDualExtensions(fileName)) {
      return this.invalidResult("The file contains dual extensions. Please provide a valid .xlsx file.");
    }

    if (!this.hasValidExtension(fileName)) {
      return this.invalidResult("The file does not have a valid spreadsheet extension (.xlsx).");
    }

    if (!this.isValidMimeType(file)) {
      return this.invalidResult("The provided file is not a valid spreadsheet based on its MIME type.");
    }

    const isValidSignature = await this.isValidSpreadsheetBySignature(file);
    if (!isValidSignature) {
      return this.invalidResult("The provided file content does not match a valid spreadsheet format.");
    }

    return await this.validateSpreadsheetContent(file);
  }

  // Validation helpers:

  private hasDualExtensions(fileName: string): boolean {
    return fileName.split('.').length > 2;
  }

  private hasValidExtension(fileName: string): boolean {
    return /\.xlsx$/i.test(fileName);
  }

  private isValidMimeType(file: Blob): boolean {
    console.log('Detected MIME type:', file.type);
    return this.validMimeTypes.includes(file.type);
  }

  private async isValidSpreadsheetBySignature(file: Blob): Promise<boolean> {
    const buffer = await this.readBlobAsArrayBuffer(file);
    const header = this.getFileSignature(buffer);
    console.log('File signature:', header);
    return header === this.zipSignature;
  }

  private getFileSignature(buffer: ArrayBuffer): string {
    const arr = new Uint8Array(buffer).subarray(0, 4);
    return Array.from(arr).map(byte => ('00' + byte.toString(16)).slice(-2)).join(' ');
  }

  private hasInvalidCharacters(value: string): boolean {
    const invalidPatterns = [
      /<script.*?>.*?<\/script>/gi,
      /<\/?[^>]+(>|$)/,
      /['"\\;]/,
      /onerror=|onclick=|onload=/i,
      /\/\*.*?\*\//,
      /[<>]/g
    ];
    return invalidPatterns.some(pattern => pattern.test(value));
  }

  // Spreadsheet content validation and modification:
  private async validateSpreadsheetContent(file: Blob): Promise<ValidationResult> {
    const fileContent = await this.readBlobAsArrayBuffer(file);
    const workbook = XLSX.read(fileContent, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
   const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });


    const invalidColumnName = "Invalid Characters";
    if (rows[0].indexOf(invalidColumnName) === -1) {
      rows[0].push(invalidColumnName);
    }

    let isValid = true;
    const invalidColumnIndex = rows[0].length - 1;

    for (let i = 1; i < rows.length; i++) {
      const invalidColumns = rows[i].map((cell, j) =>
        this.hasInvalidCharacters(cell) ? rows[0][j] : null
      ).filter(Boolean);

      if (invalidColumns.length > 0) {
        rows[i][invalidColumnIndex] = invalidColumns.join(', ');
        isValid = false;
      } else {
        rows[i][invalidColumnIndex] = '';
      }
    }

    let modifiedFile: Blob | null = null;
    if (!isValid) {
      modifiedFile = this.generateModifiedSpreadsheet(rows, sheetName);
    }

    return { isValid, modifiedFile };
  }

  private generateModifiedSpreadsheet(rows: string[][], sheetName: string): Blob {
    const newWorksheet = XLSX.utils.aoa_to_sheet(rows);
    const newWorkbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, sheetName);
    const updatedFile = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([updatedFile], { type: this.validMimeTypes[0] });
  }

  private readBlobAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  private invalidResult(message: string): ValidationResult {
    return {
      isValid: false,
      modifiedFile: null,
      errorMessage: message
    };
  }
}
